/**
 * Offline Indicator Component
 * 
 * Displays persistent indicator when offline with sync status and pending actions.
 * Shows queue display for background sync items.
 * 
 * @author AI Assistant
 * @version 2.0.0
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Collapse,
  LinearProgress,
  Tooltip,
  Badge,
  useTheme,
  alpha
} from '@mui/material';
import {
  CloudOff,
  CloudQueue,
  CloudDone,
  Sync,
  ExpandMore,
  ExpandLess,
  Refresh
} from '@mui/icons-material';
import { backgroundSyncService } from '../../services/backgroundSyncService';
// import { logger } from "../../utils/logger";
// =============================================================================
// TYPES
// =============================================================================

interface OfflineIndicatorProps {
  position?: 'top' | 'bottom';
  persistent?: boolean;
}

// =============================================================================
// OFFLINE INDICATOR COMPONENT
// =============================================================================

export const OfflineIndicator: React.FC<OfflineIndicatorProps> = ({
  position = 'bottom',
  persistent = false
}) => {
  const theme = useTheme();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);

  // =============================================================================
  // EFFECTS
  // =============================================================================

  useEffect(() => {
    // Update online status
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Update pending count
    updatePendingCount();
    const countInterval = setInterval(updatePendingCount, 5000);

    // Listen to sync events
    const syncListener = (event: string, data?: any) => {
      switch (event) {
        case 'started':
          setIsSyncing(true);
          setSyncProgress(0);
          break;
        case 'progress':
          setSyncProgress(data?.progress || 0);
          break;
        case 'completed':
          setIsSyncing(false);
          setSyncProgress(100);
          setLastSyncTime(new Date());
          updatePendingCount();
          // Reset progress after a delay
          setTimeout(() => setSyncProgress(0), 2000);
          break;
        case 'failed':
          setIsSyncing(false);
          setSyncProgress(0);
          break;
      }
    };

    backgroundSyncService.addEventListener(syncListener);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(countInterval);
      backgroundSyncService.removeEventListener(syncListener);
    };
  }, []);

  // =============================================================================
  // HANDLERS
  // =============================================================================

  const updatePendingCount = async () => {
    const count = await backgroundSyncService.getPendingCount();
    setPendingCount(count);
  };

  const handleSync = async () => {
    if (!isOnline || isSyncing) {
      return;
    }
    await backgroundSyncService.sync();
  };

  const handleToggleExpanded = () => {
    setExpanded(!expanded);
  };

  // =============================================================================
  // RENDER CONDITIONS
  // =============================================================================

  // Don't show if online and no pending items (unless persistent)
  if (isOnline && pendingCount === 0 && !persistent) {
    return null;
  }

  // =============================================================================
  // RENDER
  // =============================================================================

  const getStatusColor = () => {
    if (!isOnline) return theme.palette.error.main;
    if (isSyncing) return theme.palette.info.main;
    if (pendingCount > 0) return theme.palette.warning.main;
    return theme.palette.success.main;
  };

  const getStatusIcon = () => {
    if (!isOnline) return <CloudOff />;
    if (isSyncing) return <CloudQueue />;
    if (pendingCount > 0) return <CloudQueue />;
    return <CloudDone />;
  };

  const getStatusText = () => {
    if (!isOnline) return 'Offline';
    if (isSyncing) return 'Syncing...';
    if (pendingCount > 0) return `${pendingCount} pending`;
    return 'All synced';
  };

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'fixed',
        [position]: 16,
        left: 16,
        right: 'auto',
        zIndex: theme.zIndex.snackbar,
        width: 360,
        maxWidth: 'calc(100vw - 32px)',
        borderRadius: 2,
        overflow: 'hidden',
        backgroundColor: alpha(theme.palette.background.paper, 0.95),
        backdropFilter: 'blur(8px)',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          backgroundColor: alpha(getStatusColor(), 0.1),
          borderBottom: `2px solid ${alpha(getStatusColor(), 0.3)}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              color: getStatusColor(),
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {getStatusIcon()}
          </Box>
          <Box>
            <Typography variant="subtitle2" fontWeight={600}>
              {getStatusText()}
            </Typography>
            {lastSyncTime && isOnline && pendingCount === 0 && (
              <Typography variant="caption" color="text.secondary">
                Last synced {lastSyncTime.toLocaleTimeString()}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {isOnline && pendingCount > 0 && (
            <Tooltip title="Sync now">
              <IconButton
                size="small"
                onClick={handleSync}
                disabled={isSyncing}
                sx={{ color: theme.palette.primary.main }}
              >
                <Badge badgeContent={pendingCount} color="warning" max={99}>
                  <Sync
                    sx={{
                      animation: isSyncing ? 'spin 1s linear infinite' : 'none',
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                      },
                    }}
                  />
                </Badge>
              </IconButton>
            </Tooltip>
          )}

          {pendingCount > 0 && (
            <IconButton size="small" onClick={handleToggleExpanded}>
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Progress bar */}
      {isSyncing && (
        <LinearProgress
          variant={syncProgress > 0 ? 'determinate' : 'indeterminate'}
          value={syncProgress}
          sx={{
            height: 2,
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
          }}
        />
      )}

      {/* Expanded details */}
      <Collapse in={expanded}>
        <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            Pending Actions
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {!isOnline && (
              <Chip
                icon={<CloudOff />}
                label="Your changes will sync when you're back online"
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
            {isOnline && pendingCount > 0 && (
              <Chip
                icon={<CloudQueue />}
                label={`${pendingCount} ${pendingCount === 1 ? 'item' : 'items'} waiting to sync`}
                size="small"
                color="info"
                variant="outlined"
              />
            )}
          </Box>
        </Box>
      </Collapse>

      {/* Offline message */}
      {!isOnline && !expanded && (
        <Box sx={{ p: 2, pt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            You're offline. Changes will sync automatically when you reconnect.
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default OfflineIndicator;

